import { useState, useEffect } from 'react';
import { wordpressAPI } from '../../../services/wordpressApi';
import { WPMedia } from '../../../types/wordpress';
import { ImageComponentContent } from '../../../types/elearning';
import LoadingSkeleton from '../../ui/LoadingSkeleton';

interface ImageBlockViewerProps {
  content: ImageComponentContent;
}

const sizeClasses: Record<string, string> = {
  small: 'max-w-xs',
  medium: 'max-w-md',
  large: 'max-w-2xl',
  full: 'w-full',
};

const alignmentClasses: Record<string, string> = {
  left: 'mr-auto',
  center: 'mx-auto',
  right: 'ml-auto',
};

export default function ImageBlockViewer({ content }: ImageBlockViewerProps) {
  const [media, setMedia] = useState<WPMedia | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchMedia = async () => {
      try {
        setLoading(true);
        setError(null);
        const mediaData = await wordpressAPI.fetchMedia(content.mediaId);
        setMedia(mediaData);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to load image'));
      } finally {
        setLoading(false);
      }
    };

    fetchMedia();
  }, [content.mediaId]);

  if (loading) {
    return (
      <div className={`${sizeClasses[content.size]} ${alignmentClasses[content.alignment]} mb-4`}>
        <LoadingSkeleton lines={1} className="h-48" />
      </div>
    );
  }

  if (error || !media) {
    return (
      <div className={`${sizeClasses[content.size]} ${alignmentClasses[content.alignment]} mb-4`}>
        <div className="bg-gray-100 border border-gray-200 rounded-lg p-4 text-center">
          <p className="text-sm text-gray-500">Image not available</p>
        </div>
      </div>
    );
  }

  return (
    <figure className={`${sizeClasses[content.size]} ${alignmentClasses[content.alignment]} mb-4`}>
      <img
        src={media.source_url}
        alt={content.alt}
        className="w-full h-auto rounded-lg"
        loading="lazy"
      />
      {content.caption && (
        <figcaption className="text-sm text-gray-600 text-center mt-2">
          {content.caption}
        </figcaption>
      )}
    </figure>
  );
}

